// workdayStore.ts — Globaali työpäivän tila (localStorage-pohjainen)
// Säilyy sivujen välillä, ei nollaudu navigoidessa
//
// Tauko (21.8.2026): pausedAt != null = tauko käynnissä, pausedSecondsTotal =
// päättyneiden taukojen kertymä sekunteina. Kulunut työaika =
// now − startedAt − pausedSecondsTotal − (pausedAt ? now − pausedAt : 0).
// Sama laskukaava kuin backendin WorkSessionissa (ja NssTest-esikuvassa).

import { useState, useEffect } from 'react';

const KEY = 'savroc_workday';

export interface WorkdayState {
  startedAt: string;    // ISO timestamp
  isWorking: boolean;
  pausedAt?: string | null;      // ISO timestamp — tauko käynnissä jos asetettu
  pausedSecondsTotal?: number;   // päättyneiden taukojen kertymä sekunteina
}

export function getWorkday(): WorkdayState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveWorkday(state: WorkdayState): void {
  localStorage.setItem(KEY, JSON.stringify(state));
  window.dispatchEvent(new Event('workday-change'));
}

export function startWorkday(
  startedAt?: string,
  pause?: { pausedAt?: string | null; pausedSecondsTotal?: number },
): WorkdayState {
  // Jos startedAt annettu (esim. synkronoidessa backendin WorkSessionista toisella laitteella),
  // käytetään sitä. Muutoin käytetään tämän laitteen nykyistä kellonaikaa (uusi clock-in).
  const state: WorkdayState = {
    startedAt: startedAt ?? new Date().toISOString(),
    isWorking: true,
    pausedAt: pause?.pausedAt ?? null,
    pausedSecondsTotal: pause?.pausedSecondsTotal ?? 0,
  };
  saveWorkday(state);
  return state;
}

/** Aloita tauko paikallisesti — kello pysähtyy. Ei-op jos jo tauolla. */
export function pauseWorkday(pausedAt?: string): void {
  const wd = getWorkday();
  if (!wd || wd.pausedAt) return;
  saveWorkday({ ...wd, pausedAt: pausedAt ?? new Date().toISOString() });
}

/** Päätä tauko paikallisesti — tauon kesto siirtyy kertymään. Ei-op jos ei tauolla. */
export function resumeWorkday(): void {
  const wd = getWorkday();
  if (!wd || !wd.pausedAt) return;
  const pauseSecs = Math.max(0, Math.floor((Date.now() - new Date(wd.pausedAt).getTime()) / 1000));
  saveWorkday({
    ...wd,
    pausedAt: null,
    pausedSecondsTotal: (wd.pausedSecondsTotal ?? 0) + pauseSecs,
  });
}

// Synkronoi localStorage backendin palauttaman tilan kanssa.
// - Jos backend palauttaa aktiivisen session, käytetään sen startedAt/tauko-tietoja
//   (toinen laite voi olla aloittanut työn tai tauon)
// - Jos backend palauttaa null (ei aktiivista), tyhjennetään localStorage (toinen laite on ehkä lopettanut)
export function syncWorkdayWithBackend(
  activeSession: { startedAt?: string | null; pausedAt?: string | null; pausedSecondsTotal?: number | null } | null,
): void {
  const wd = getWorkday();
  if (activeSession && activeSession.startedAt) {
    const backendStarted = new Date(activeSession.startedAt).toISOString();
    const backendPaused = activeSession.pausedAt ? new Date(activeSession.pausedAt).toISOString() : null;
    const backendPauseTotal = activeSession.pausedSecondsTotal ?? 0;
    // Ei kirjoiteta turhaan jos localStorage on jo oikein
    if (
      !wd
      || wd.startedAt !== backendStarted
      || (wd.pausedAt ?? null) !== backendPaused
      || (wd.pausedSecondsTotal ?? 0) !== backendPauseTotal
    ) {
      startWorkday(backendStarted, { pausedAt: backendPaused, pausedSecondsTotal: backendPauseTotal });
    }
  } else {
    // Backend sanoo ei aktiivista → paikallinen tila pitäisi nollata
    if (wd) endWorkday();
  }
}

export function endWorkday(): void {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new Event('workday-change'));
}

// Hook — kuuntelee muutoksia kaikista komponenteista
export function useWorkday(): WorkdayState | null {
  const [state, setState] = useState<WorkdayState | null>(getWorkday);

  useEffect(() => {
    const handler = () => setState(getWorkday());
    window.addEventListener('workday-change', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('workday-change', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  return state;
}

// Laskettu kulunut TYÖaika sekunteina — tauot eivät kerry (21.8.2026).
export function getElapsedSeconds(wd: WorkdayState | null): number {
  if (!wd) return 0;
  const now = Date.now();
  const pausedMs = (wd.pausedSecondsTotal ?? 0) * 1000
    + (wd.pausedAt ? Math.max(0, now - new Date(wd.pausedAt).getTime()) : 0);
  return Math.max(0, Math.floor((now - new Date(wd.startedAt).getTime() - pausedMs) / 1000));
}

/** Käynnissä olevan tauon kesto sekunteina (0 jos ei tauolla). */
export function getBreakSeconds(wd: WorkdayState | null): number {
  if (!wd?.pausedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(wd.pausedAt).getTime()) / 1000));
}

export function formatElapsed(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
